import { useEffect, useMemo, useRef, type KeyboardEvent } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
} from '@monksflow/monks-ui';
import { ChevronDown } from 'lucide-react';
import { useForm } from 'react-hook-form';
import type { ProjectListItem } from '@/entities/project';
import { useWorkspaceTagSuggestions } from '@/entities/project-tag';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { useUpdatePublishedProject } from '../../api/useUpdatePublishedProject.mutation';
import {
  buildQuickEditProjectFormSchema,
  type QuickEditProjectFormData,
} from './quick-edit-project-form.schema';
import { useTranslation } from 'react-i18next';

interface QuickEditProjectModalProps {
  open: boolean;
  project: ProjectListItem;
  workspaceId?: string | null;
  onClose: () => void;
  onSuccess?: (project: ProjectListItem) => void;
}

export function QuickEditProjectModal(props: QuickEditProjectModalProps) {
  const { t } = useTranslation('projects');

  const workspaceId = useCurrentWorkspaceId();
  const { suggestions } = useWorkspaceTagSuggestions(workspaceId);
  const updateMutation = useUpdatePublishedProject({
    workspaceId: props.workspaceId ?? workspaceId,
  });

  const defaultValues = useMemo<QuickEditProjectFormData>(
    () => ({
      name: props.project.title ?? '',
      tags: props.project.tags.map((tag) => tag.name),
    }),
    [props.project],
  );

  const formSchema = useMemo(() => buildQuickEditProjectFormSchema(t), [t]);

  const form = useForm<QuickEditProjectFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
    // Submit-time rather than `onTouched`: a project carries no title until it
    // leaves draft, so this modal can open on an empty name. The dialog
    // autofocuses that field, so a blur-time check fires the moment the user
    // reaches for the close button, and the message it renders grows the
    // centred dialog enough to slide that 16px button out from under the
    // pointer — swallowing the very click that asked to close. The CTA already
    // stays disabled while the name is empty, so the blur check told the user
    // nothing they could act on.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (props.open) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form, props.open]);

  const tagsAnchorRef = useRef<HTMLDivElement | null>(null);

  const isPending = updateMutation.isPending;
  const watchedName = form.watch('name');
  const canSubmit = !isPending && watchedName.trim().length > 0;

  const handleMutationError = (error: unknown) => {
    const { status, detail } = parseProblemDetail(
      error,
      t('editProject.quickEditProjectModal.failedToUpdateProject'),
    );
    if (status === 409) {
      form.setError('name', { type: 'server', message: detail });
      return;
    }
    notificationService.showError(detail);
  };

  // `onClose` alone — the host unmounts on its own timer. See `useModalState`.
  const handleClose = () => {
    if (isPending) return;
    props.onClose();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) handleClose();
  };

  const onSubmit = form.handleSubmit((formData) => {
    const platformProjectId = props.project.platformProjectId;
    if (!platformProjectId) return;
    updateMutation.mutate(
      {
        projectId: platformProjectId,
        payload: { title: formData.name, tags: formData.tags },
      },
      {
        onSuccess: () => {
          props.onSuccess?.(props.project);
          handleClose();
        },
        onError: handleMutationError,
      },
    );
  });

  return (
    <Dialog open={props.open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="gap-0 p-4 max-w-120"
        showCloseButton={!isPending}
        onEscapeKeyDown={(event) => {
          if (isPending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('editProject.quickEditProjectModal.editProject')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              void onSubmit(event);
            }}
            className="flex flex-col"
          >
            <DialogBody className="flex flex-col gap-4 py-10">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('editProject.quickEditProjectModal.projectName')}
                      <span aria-hidden="true" className="text-destructive">
                        *
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('editProject.quickEditProjectModal.enterTheProjectName')}
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => {
                  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
                    if (event.key !== 'Enter') return;
                    const target = event.currentTarget;
                    const text = target.value.trim();
                    if (text === '') return;
                    if (field.value.includes(text)) {
                      target.value = '';
                      return;
                    }
                    event.preventDefault();
                    field.onChange([...field.value, text]);
                    target.value = '';
                  };

                  return (
                    <FormItem>
                      <FormLabel>{t('editProject.quickEditProjectModal.tags')}</FormLabel>
                      <FormControl>
                        <Combobox<string, true>
                          multiple
                          value={field.value}
                          onValueChange={field.onChange}
                          items={suggestions}
                          disabled={isPending}
                          itemToStringLabel={(item) => item}
                          itemToStringValue={(item) => item}
                        >
                          <ComboboxChips ref={tagsAnchorRef}>
                            {field.value.map((tag, index) => (
                              <ComboboxChip
                                key={`${tag}:${index}`}
                                aria-label={t('editProject.quickEditProjectModal.removeTag', {
                                  tag,
                                })}
                                className="rounded-full bg-accent px-2.5 text-accent-foreground"
                              >
                                {tag}
                              </ComboboxChip>
                            ))}
                            <ComboboxChipsInput
                              placeholder={t('editProject.quickEditProjectModal.enterTags')}
                              disabled={isPending}
                              onKeyDown={handleKeyDown}
                            />
                            <ChevronDown
                              aria-hidden="true"
                              className="ml-auto size-4 shrink-0 text-foreground-subtle pointer-events-none"
                            />
                          </ComboboxChips>
                          <ComboboxContent anchor={tagsAnchorRef} className="pointer-events-auto">
                            <ComboboxList>
                              <ComboboxCollection>
                                {(item: string) => (
                                  <ComboboxItem key={item} value={item}>
                                    {item}
                                  </ComboboxItem>
                                )}
                              </ComboboxCollection>
                              <ComboboxEmpty>
                                {t('editProject.quickEditProjectModal.noResults')}
                              </ComboboxEmpty>
                            </ComboboxList>
                          </ComboboxContent>
                        </Combobox>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
                {t('editProject.quickEditProjectModal.cancel')}
              </Button>
              <Button type="submit" variant="default" disabled={!canSubmit}>
                {isPending && <Spinner />} {t('editProject.quickEditProjectModal.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
